import * as React from "react";
import { supabase } from "./supabase";

/**
 * Enrolling and removing a TOTP factor, for the account page.
 *
 * Separate from `AuthProvider` on purpose. That context owns the *session* -
 * who is signed in and what they may do - and every screen in the product reads
 * it. This is one screen's worth of setup state, so putting it there would mean
 * every consumer of `useAuth()` re-rendering when somebody opens a QR code.
 *
 * The challenge half of 2FA is **not** here: it belongs to the session and lives
 * in `auth.tsx` as `verifyMfa`, because it decides whether the console renders
 * at all.
 */

export interface TotpSetup {
  factorId: string;
  /** An SVG data URL, rendered by GoTrue. No QR library needed. */
  qr: string;
  /** The same secret as text, for a phone that cannot scan its own screen. */
  secret: string;
}

interface Factor {
  id: string;
  friendlyName?: string;
}

export function useMfa() {
  const [factors, setFactors] = React.useState<Factor[]>([]);

  /**
   * Verified factors only.
   *
   * `listFactors()` returns unverified ones too - an enrolment somebody started
   * and abandoned - and counting those would show "two-step verification is on"
   * for an account that will never be asked for a code.
   */
  const refreshFactors = React.useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;

    setFactors(
      (data?.totp ?? [])
        .filter((factor) => factor.status === "verified")
        .map((factor) => ({ id: factor.id, friendlyName: factor.friendly_name ?? undefined }))
    );
  }, []);

  React.useEffect(() => {
    void refreshFactors().catch(() => setFactors([]));
  }, [refreshFactors]);

  const enrollTotp = React.useCallback(async (): Promise<TotpSetup> => {
    // A name GoTrue requires to be unique per user, so an abandoned enrolment
    // that was not cleaned up would make the next attempt fail rather than
    // start. The timestamp keeps them apart; the user never sees it.
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: `TaskIQ ${new Date().toISOString()}`,
    });
    if (error) throw error;

    return {
      factorId: data.id,
      qr: data.totp.qr_code,
      secret: data.totp.secret,
    };
  }, []);

  /**
   * Prove the secret arrived, which is what turns the factor on.
   *
   * `challengeAndVerify` rather than the two calls separately: a challenge that
   * is not immediately verified is only an id to lose track of.
   */
  const confirmTotp = React.useCallback(async (factorId: string, code: string) => {
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: code.replace(/\D/g, ""),
    });
    if (error) throw error;
  }, []);

  const disableTotp = React.useCallback(async (factorId: string) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) throw error;
  }, []);

  return { factors, refreshFactors, enrollTotp, confirmTotp, disableTotp };
}
